import { Input, Radio, Select } from 'antd';
import { Controller } from 'react-hook-form';
import { useExtensionBoxForm } from './hooks-form';
import LabelRequired from '../../Common/LabelRequired';
import { forwardRef, useImperativeHandle } from 'react';

interface ExtensionBoxDownloadProps {
  serialType: 'camera' | 'hardware';
  serialNumber: string;
  onSerialTypeChange: (value: 'camera' | 'hardware') => void;
  onSerialNumberChange: (value: string) => void;
  notificationEmail: string;
  onNotificationEmailChange: (value: string) => void;
  agentName: string;
  onAgentNameChange: (value: string) => void;
  agencyContactPerson: string;
  onAgencyContactPersonChange: (value: string) => void;
  agencyEmail: string;
  onAgencyEmailChange: (value: string) => void;
  clinicName: string;
  onClinicNameChange: (value: string) => void;
  userContactName: string;
  onUserContactNameChange: (value: string) => void;
  userContactEmail: string;
  onUserContactEmailChange: (value: string) => void;
}

export interface ExtensionBoxDownloadRef {
  validate: () => Promise<boolean>;
}

const ExtensionBoxDownload = forwardRef<ExtensionBoxDownloadRef, ExtensionBoxDownloadProps>(
  (
    {
      serialType,
      serialNumber,
      onSerialTypeChange,
      onSerialNumberChange,
      notificationEmail,
      onNotificationEmailChange,
      agentName,
      onAgentNameChange,
      agencyContactPerson,
      onAgencyContactPersonChange,
      agencyEmail,
      onAgencyEmailChange,
      clinicName,
      onClinicNameChange,
      userContactName,
      onUserContactNameChange,
      userContactEmail,
      onUserContactEmailChange,
    },
    ref
  ) => {
    const { control, errors, emailOptions, handleEmailBlur, trigger } = useExtensionBoxForm({
      defaultValues: {
        serialNumber,
        notificationEmail,
        agentName,
        agencyContactPerson,
        agencyEmail,
        clinicName,
        userContactName,
        userContactEmail,
      },
    });

    useImperativeHandle(ref, () => ({
      validate: async () => {
        return await trigger();
      },
    }));

    return (
      <div className='p-6 bg-[#F4F6F9] border border-[#BCC2FF] rounded-2xl flex gap-6'>
        <div className='w-1/2 mt-[7px] flex flex-col space-y-4'>
          <Radio.Group
            className='flex flex-col gap-2'
            value={serialType}
            onChange={(e) => onSerialTypeChange(e.target.value)}
          >
            <Radio
              value='hardware'
              className={`text-lg text-[#2C3A4B] ${
                serialType === 'hardware' ? 'font-bold' : 'font-medium'
              }`}
            >
              Hardware Serial Number
            </Radio>
            <Radio
              value='camera'
              className={`text-lg text-[#2C3A4B] ${
                serialType === 'camera' ? 'font-bold' : 'font-medium'
              }`}
            >
              Camera Serial Number
            </Radio>
          </Radio.Group>

          <Controller
            name='serialNumber'
            control={control}
            render={({ field }) => (
              <div>
                <Input
                  {...field}
                  type='text'
                  className='w-full !h-[46px]'
                  size='large'
                  status={errors.serialNumber ? 'error' : ''}
                  placeholder='Enter serial number'
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e);
                    onSerialNumberChange(e.target.value);
                  }}
                />
                {errors.serialNumber && (
                  <p className='mt-1 text-sm text-red-500'>{errors.serialNumber.message}</p>
                )}
              </div>
            )}
          />

          <div>
            <LabelRequired text='Agent name' />
            <Controller
              name='agentName'
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type='text'
                    className='w-full h-[46px]'
                    status={errors.agentName ? 'error' : ''}
                    placeholder='Enter agent name'
                    value={field.value || ''}
                    disabled
                    onChange={(e) => {
                      field.onChange(e);
                      onAgentNameChange(e.target.value);
                    }}
                  />
                  {errors.agentName && (
                    <p className='mt-1 text-sm text-red-500'>{errors.agentName.message}</p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text='Name of person in charge of the agency' />
            <Controller
              name='agencyContactPerson'
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type='text'
                    className='w-full h-[46px]'
                    status={errors.agencyContactPerson ? 'error' : ''}
                    placeholder='Enter name of person in charge of the agency'
                    value={field.value || ''}
                    disabled
                    onChange={(e) => {
                      field.onChange(e);
                      onAgencyContactPersonChange(e.target.value);
                    }}
                  />
                  {errors.agencyContactPerson && (
                    <p className='mt-1 text-sm text-red-500'>
                      {errors.agencyContactPerson.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text='Agency contact email address' />
            <Controller
              name='agencyEmail'
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type='email'
                    className='w-full h-[46px]'
                    status={errors.agencyEmail ? 'error' : ''}
                    placeholder='Enter agency contact email address'
                    value={field.value || ''}
                    disabled
                    onChange={(e) => {
                      field.onChange(e);
                      onAgencyEmailChange(e.target.value);
                    }}
                  />
                  {errors.agencyEmail && (
                    <p className='mt-1 text-sm text-red-500'>{errors.agencyEmail.message}</p>
                  )}
                </div>
              )}
            />
          </div>
        </div>
        <div className='flex flex-col space-y-4 w-1/2 mt-[51px]'>
          <div>
            <LabelRequired text='End User (Clinic) Name' required />
            <Controller
              name='clinicName'
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type='text'
                    className='w-full h-[46px]'
                    status={errors.clinicName ? 'error' : ''}
                    placeholder='Enter end user (clinic) name'
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e);
                      onClinicNameChange(e.target.value);
                    }}
                  />
                  {errors.clinicName && (
                    <p className='mt-1 text-sm text-red-500'>{errors.clinicName.message}</p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text='End user contact name' required={false} />
            <Controller
              name='userContactName'
              control={control}
              render={({ field }) => (
                <div className={errors.agentName ? 'mb-[38px]' : ''}>
                  <Input
                    {...field}
                    type='text'
                    className='w-full h-[46px]'
                    status={errors.userContactName ? 'error' : ''}
                    placeholder='Enter end user contact name'
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e);
                      onUserContactNameChange(e.target.value);
                    }}
                  />
                  {errors.userContactName && (
                    <p className='mt-1 text-sm text-red-500'>{errors.userContactName.message}</p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired required={false} text='End user contact email address' />
            <Controller
              name='userContactEmail'
              control={control}
              render={({ field }) => (
                <div className={errors.agencyContactPerson ? 'mb-[38px]' : ''}>
                  <Input
                    {...field}
                    type='email'
                    placeholder='Enter email address'
                    className='w-full h-[46px]'
                    onBlur={(e) => {
                      field.onBlur();
                      handleEmailBlur();
                    }}
                    status={errors.userContactEmail ? 'error' : ''}
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e);
                      onUserContactEmailChange(e.target.value);
                    }}
                  />
                  {errors.userContactEmail && (
                    <p className='mt-1 text-sm text-red-500'>{errors.userContactEmail.message}</p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text='Email address to send download information' required />
            <Controller
              name='notificationEmail'
              control={control}
              render={({ field }) => (
                <div>
                  <Select
                    {...field}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      onNotificationEmailChange(value as string);
                    }}
                    className='!w-full !h-[46px]'
                    options={emailOptions}
                    notFoundContent={<div className='p-2 text-center text-gray-500'>No Data</div>}
                    status={errors.notificationEmail ? 'error' : ''}
                  />
                  {errors.notificationEmail && (
                    <p className='mt-1 text-sm text-red-500'>{errors.notificationEmail.message}</p>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </div>
    );
  }
);

export default ExtensionBoxDownload;
